import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as authActions from "../../../store/auth/auth.actions";
import * as userActions from "../../../store/user/user.actions";
import { Observable, Subscription, Subject, takeUntil } from 'rxjs';
import { IClientInfo, ITempTokenRequestBody } from '../../../models/authentication';
import { selectAuthStatus, selectClientInfo } from '../../../store/auth/auth.reducer';
import { AuthStatus } from '../../../models/authentication';
import { AuthPaths } from '../authentication-routing.module'; // Import RoutesPath
import { DefaultAuthSignInRegisterComponentResource, AuthSignInRegisterComponentResources } from './auth-signin-register.component.resources';
import { AuthService } from '../../../services/auth.service';
import { config } from '../../../../config/appConfig';
import { Actions } from '@ngrx/effects';
import { SnackBarService } from '../../../services/snackbar.service';
import { UserFormDetails } from '../register/register.component';
import { IUpdateUserInfo, IUserInfo } from '../../../models/user';
import { selectUserInfo } from '../../../store/user/user.reducer';
import { UserService } from '../../../services/user.service';
import { AppleSignInFailure, AppleSignInSuccess } from '../apple-signin/apple-signin.component';


@Component({
  selector: 'app-auth-signin-register',
  standalone: false,
  templateUrl: './auth-signin-register.component.html',
  styleUrls: ['./auth-signin-register.component.scss']
})
export class AuthSigninRegisterComponent implements OnInit {
  accountCreationForm: FormGroup;
  isOtpScreen = false;
  errorMessage: string | null = null;
  isSignInRoute: boolean = false;
  isRegisterRoute: boolean = false;

  userInfo$: Observable<IUserInfo | null>;
  registerFormData: UserFormDetails = {
    fullName: null,
    mobileNumber: null,
    email: null
  }

  // Component Resources
  finalResources!: AuthSignInRegisterComponentResources;

  // Input And Output
  @Input() resources: Partial<AuthSignInRegisterComponentResources> = {};


  // Observables
  clientInfo$: Observable<IClientInfo | null>;
  authStatus$: Observable<AuthStatus>;
  private _subscriptions: Subscription[] = [];
  loginHint: string = "";
  clientInfo: IClientInfo | null;

  private destroy$ = new Subject<void>();
  shouldAllowLogin: boolean = true;
  appleClientId: string = config.appleClientId;
  allowAppleSignIn: boolean = true;       // Currently Apple Sign In Allowed for All Countries

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public route: ActivatedRoute,
    private userService: UserService,
    private store: Store,
    private authService: AuthService,
    private actions$: Actions,
    private snackBar: SnackBarService
  ) {
    this.accountCreationForm = this.fb.group({
      emailOrMobile: ['', [Validators.required, Validators.email]],
      otp: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.clientInfo$ = this.store.select(selectClientInfo);
    this.authStatus$ = this.store.select(selectAuthStatus);
    this.clientInfo = null;
    this.userInfo$ = this.store.select(selectUserInfo);
  }


  // When the Login Using Google Is Successful
  onGoogleLoginSuccess(response: any): void {
    if (response.code) {
      //fetch token req
      let fetchReq: ITempTokenRequestBody = {
        AuthCode: response.code,
        RedirectUri: config.authRedirectUri
      }
      this.store.dispatch(authActions.FetchTempTokenAction({ request: fetchReq, provider: 'google' }));
    }
  }

  onGoogleLoginFailure(response: any): void {
    const msg = response.error === "access_denied" ? "(Access Denied)" : response.message ? `(${response.message})` : "";
    this.snackBar.showSnackBar("error", `${this.finalResources.googleLoginErrorMessage} ${msg}`);
  }

  // In your parent component
  onAppleSuccess(result: AppleSignInSuccess): void {
    this.store.dispatch(authActions.FetchTempTokenAction({
      request: {
        AuthCode: result.code,
        RedirectUri: window.location.origin,
        AppleUser: result.user
      },
      provider: 'apple'
    }));
  }

  onAppleFailure(result: AppleSignInFailure): void {
    switch (result.error) {
      // User-initiated
      case 'popup_closed_by_user':
      case 'user_cancelled_authorize':
        this.snackBar.showSnackBar('error', 'Apple Sign In cancelled.');
        break;

      //Network / loading issues
      case 'sdk_load_failed':
        this.snackBar.showSnackBar('error', 'Apple Sign-In is currently unavailable. Please try again later.');
        break;

      // ── Configuration issues
      case 'init_failed':
      case 'invalid_client':
        this.snackBar.showSnackBar('error', 'Apple Sign-In is currently unavailable. Please try again later.');
        break;

      case 'invalid_request':
        this.snackBar.showSnackBar('error', 'Something went wrong. Please try again.');
        break;

      // Apple response issue
      case 'no_auth_code':
        this.snackBar.showSnackBar('error', 'Sign-In was incomplete. Please try again.');
        break;

      // Popup issues
      case 'popup_blocked':
        this.snackBar.showSnackBar('error', 'Popup was blocked. Please allow popups and try again.');
        break;

      // Catch-all
      default:
        this.snackBar.showSnackBar('error', 'Apple Sign-In failed. Please try again.');
        break;
    }
  }

  handleRegisterFormSubmit($event: UserFormDetails) {
    this.registerFormData = $event; // Assign directly

    // Get Stored User Data
    let userData = this.userService.getUserInfo();

    let formData: IUpdateUserInfo = {
      Email: $event.email,
      Mobile: $event.mobileNumber,
      Fullname: $event.fullName,
      Declarations: {
        IsAbove18: true,
        IsMarketingOptedIn: $event.IsMarketingOptedIn ?? true
      },
      PreferredLanguage: $event.preferredLanguage
    }
    this.store.dispatch(userActions.UpdateUserAction({
      request: formData,
      userId: userData?.UserId!
    }))

  }

  ngOnInit(): void {

    // Client Info
    this._subscriptions.push(
      this.clientInfo$.subscribe((clientInfo) => {
        if (clientInfo) {
          this.clientInfo = clientInfo;
          this.shouldAllowLogin = Object.values(clientInfo.LoginMethodsAllowed).some(method => method === true);
        }
      })
    )

    // User Info
    this._subscriptions.push(
      this.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((userInfo) => {
        if (userInfo) {
          this.registerFormData = {
            ...this.registerFormData,
            email: userInfo.Email,
            fullName: userInfo.Fullname,
            mobileNumber: userInfo.Mobile
          };
        }
      })
    );

    // Set isSignIn based on route path
    this.isSignInRoute = this.route.snapshot.url[0].path === AuthPaths.signIn;
    this.isRegisterRoute = this.route.snapshot.url[0].path === AuthPaths.register;

    // Set the Login Hint As User's Sub
    this.loginHint = this.authService.getUserSub() || "";

    this.finalResources = { ...DefaultAuthSignInRegisterComponentResource, ...this.resources };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
